import { ICellRendererComp, ICellRendererParams } from '@ag-grid-community/core';
import {
  ORDER_DRAFT_PILL_BG,
  ORDER_DRAFT_PILL_TEXT,
  ORDER_SUBMITTED_PILL_BG,
  ORDER_SUBMITTED_PILL_TEXT,
} from '../../../styles';
import {
  classifyMyCpOrderQtyDisplay,
  formatCpQtyForPillDisplay,
  isMyCpOrderPillGreen,
  parseMyCpOrderDisplayQty,
  toMyCpQtyBuckets,
} from '../deals.util';

/** Fixed capsule dimensions — numbers truncate with ellipsis; cell tooltip carries details. */
const PILL_W_PX = 80;
const PILL_H_PX = 22;

/**
 * Single fixed pill showing ORDER_DISPLAY_QTY (per-order max, then sum on server).
 * Amber when NON_SUCCESS_ORDER_COUNT > 0; green when total qty > 0 and no non-success orders.
 */
export class CounterpartyOrderStatusRenderer implements ICellRendererComp {
  private eGui!: HTMLElement;

  init(params: ICellRendererParams): void {
    this.eGui = document.createElement('div');
    this.eGui.style.display = 'flex';
    this.eGui.style.alignItems = 'center';
    this.eGui.style.justifyContent = 'center';
    this.eGui.style.height = '100%';
    this.eGui.style.width = '100%';
    this.eGui.style.gap = '6px';
    this.eGui.style.flexWrap = 'nowrap';
    this.render(params);
  }

  private createPill(displayText: string, bg: string, color: string): HTMLElement {
    const el = document.createElement('div');
    el.textContent = displayText;
    el.style.width = `${PILL_W_PX}px`;
    el.style.minWidth = `${PILL_W_PX}px`;
    el.style.maxWidth = `${PILL_W_PX}px`;
    el.style.height = `${PILL_H_PX}px`;
    el.style.boxSizing = 'border-box';
    el.style.borderRadius = '999px';
    el.style.backgroundColor = bg;
    el.style.color = color;
    el.style.fontSize = '11px';
    el.style.fontWeight = '600';
    el.style.display = 'flex';
    el.style.alignItems = 'center';
    el.style.justifyContent = 'center';
    el.style.lineHeight = 'normal';
    el.style.overflow = 'hidden';
    el.style.textOverflow = 'ellipsis';
    el.style.whiteSpace = 'nowrap';
    el.style.padding = '0 8px';
    el.style.fontVariantNumeric = 'tabular-nums';
    return el;
  }

  private render(params: ICellRendererParams): void {
    this.eGui.replaceChildren();
    const buckets = toMyCpQtyBuckets(params.data?.SUBMITTED_QTY, params.data?.DRAFT_QTY);
    const displayQty = parseMyCpOrderDisplayQty(params.data?.ORDER_DISPLAY_QTY);
    const kind = classifyMyCpOrderQtyDisplay(buckets);
    if (displayQty == null && (kind === 'empty' || !buckets)) {
      return;
    }

    const pillQty = displayQty ?? buckets!.submitted + buckets!.draft;
    const totalStr = formatCpQtyForPillDisplay(pillQty);
    const pillGreen = isMyCpOrderPillGreen(
      buckets ?? { submitted: pillQty, draft: 0 },
      params.data?.NON_SUCCESS_ORDER_COUNT,
    );
    const pillBg = pillGreen ? ORDER_SUBMITTED_PILL_BG : ORDER_DRAFT_PILL_BG;
    const pillFg = pillGreen ? ORDER_SUBMITTED_PILL_TEXT : ORDER_DRAFT_PILL_TEXT;
    this.eGui.appendChild(this.createPill(totalStr, pillBg, pillFg));
  }

  getGui(): HTMLElement {
    return this.eGui;
  }

  refresh(params: ICellRendererParams): boolean {
    this.render(params);
    return true;
  }
}
